import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { MARKETING } from "@/constants/testIds";
import { toast, Toaster } from "sonner";
import { Sparkles, Copy, Trash2, Linkedin, Twitter, Facebook, Instagram, Check } from "lucide-react";

const PLATFORMS = [
  { key: "linkedin", label: "LinkedIn", icon: Linkedin, color: "#0A66C2" },
  { key: "twitter", label: "X (Twitter)", icon: Twitter, color: "#0F1419" },
  { key: "facebook", label: "Facebook", icon: Facebook, color: "#1877F2" },
  { key: "instagram", label: "Instagram", icon: Instagram, color: "#E4405F" },
];

const SUGGESTED_TOPICS = [
  "Why local businesses lose 27% of calls (and what to do about it)",
  "3 signs your business needs an AI voice agent",
  "Case study: How Diaz Plumbing booked 40 extra jobs/mo",
  "The cost of a missed call in the trades",
  "Announcing free websites for HVAC businesses",
];

export default function Marketing() {
  const [topic, setTopic] = useState("");
  const [tone, setTone] = useState("confident, direct, benefit-focused");
  const [cta, setCta] = useState("https://clientsonramp.com");
  const [selected, setSelected] = useState({
    linkedin: true, twitter: true, facebook: true, instagram: true,
  });
  const [drafts, setDrafts] = useState(null);
  const [copied, setCopied] = useState("");
  const [busy, setBusy] = useState(false);

  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);

  const loadHistory = async () => {
    try {
      const { data } = await api.get("/marketing");
      setHistory(data.posts || []);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoadingHistory(false); }
  };
  useEffect(() => { loadHistory(); }, []);

  const generate = async () => {
    if (!topic.trim()) { toast.error("Enter a topic first."); return; }
    const platforms = Object.keys(selected).filter((k) => selected[k]);
    if (platforms.length === 0) { toast.error("Pick at least one platform."); return; }
    setBusy(true);
    setDrafts(null);
    try {
      const { data } = await api.post("/marketing/generate", {
        topic, tone, cta_url: cta, platforms,
      });
      setDrafts(data.drafts);
      setHistory([data, ...history]);
      toast.success("Drafts ready.");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const copyDraft = async (platform, text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(platform);
      toast.success(`${platform} copied.`);
      setTimeout(() => setCopied(""), 2000);
    } catch { toast.error("Copy failed."); }
  };

  const loadHistoryItem = (item) => {
    setTopic(item.topic || "");
    setTone(item.tone || "");
    setCta(item.cta_url || "");
    const sel = {};
    PLATFORMS.forEach((p) => { sel[p.key] = (item.platforms || []).includes(p.key); });
    setSelected(sel);
    setDrafts(item.drafts || null);
  };

  const removeHistory = async (id) => {
    try {
      await api.delete(`/marketing/${id}`);
      setHistory(history.filter((h) => h.id !== id));
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div data-testid={MARKETING.container} className="p-10">
      <Toaster position="top-right" richColors />
      <div className="mb-8">
        <div className="overline mb-2">Back office</div>
        <h1 className="font-display text-4xl font-black tracking-tight">Social media drafts</h1>
        <p className="text-neutral-600 mt-2 max-w-2xl">
          AI-generated posts tuned per platform. Paste directly into LinkedIn, X, Facebook, or Instagram.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
        {/* Left: form */}
        <div className="lg:col-span-2 space-y-4">
          <div className="bg-white border border-neutral-200 p-6">
            <div className="overline mb-4">Prompt</div>
            <div className="space-y-4">
              <Field label="Topic / hook">
                <textarea
                  data-testid={MARKETING.topicInput}
                  value={topic}
                  onChange={(e) => setTopic(e.target.value)}
                  rows={3}
                  placeholder="e.g. Why local businesses lose 27% of calls…"
                  className="lf-input"
                />
                <div className="mt-2 flex flex-wrap gap-1">
                  {SUGGESTED_TOPICS.map((s) => (
                    <button
                      key={s}
                      onClick={() => setTopic(s)}
                      className="text-xs px-2 py-1 border border-neutral-300 hover:border-black hover:bg-neutral-50 transition"
                    >
                      {s.length > 42 ? s.slice(0, 42) + "…" : s}
                    </button>
                  ))}
                </div>
              </Field>
              <Field label="Tone">
                <input
                  data-testid={MARKETING.toneInput}
                  value={tone}
                  onChange={(e) => setTone(e.target.value)}
                  className="lf-input"
                />
              </Field>
              <Field label="CTA link">
                <input
                  data-testid={MARKETING.ctaInput}
                  value={cta}
                  onChange={(e) => setCta(e.target.value)}
                  className="lf-input"
                />
              </Field>
              <div>
                <span className="block text-xs font-semibold tracking-widest uppercase text-neutral-500 mb-2">Platforms</span>
                <div className="grid grid-cols-2 gap-2">
                  {PLATFORMS.map((p) => {
                    const Icon = p.icon;
                    const on = selected[p.key];
                    return (
                      <button
                        key={p.key}
                        data-testid={MARKETING.platformToggle(p.key)}
                        onClick={() => setSelected({ ...selected, [p.key]: !on })}
                        className={`px-3 py-2 border transition text-sm flex items-center gap-2 ${
                          on ? "border-black bg-black text-white" : "border-neutral-300 bg-white hover:border-neutral-500"
                        }`}
                      >
                        <Icon className="w-4 h-4" />
                        {p.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              <button
                data-testid={MARKETING.generateBtn}
                onClick={generate}
                disabled={busy}
                className="lf-btn-primary w-full flex items-center justify-center gap-2"
              >
                <Sparkles className="w-4 h-4" />
                {busy ? "Writing…" : "Generate drafts"}
              </button>
            </div>
          </div>

          <div className="bg-white border border-neutral-200 p-6" data-testid={MARKETING.historyList}>
            <div className="overline mb-3">History</div>
            {loadingHistory ? (
              <p className="text-sm text-neutral-500">Loading…</p>
            ) : history.length === 0 ? (
              <p className="text-sm text-neutral-500">Nothing yet.</p>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {history.map((h) => (
                  <div
                    key={h.id}
                    data-testid={MARKETING.historyItem(h.id)}
                    className="flex items-start justify-between gap-2 border-b border-neutral-100 pb-2"
                  >
                    <button
                      onClick={() => loadHistoryItem(h)}
                      className="text-sm text-left flex-1 hover:underline"
                    >
                      {h.topic}
                    </button>
                    <div className="flex items-center gap-2">
                      <div className="text-xs text-neutral-500 font-mono whitespace-nowrap">
                        {new Date(h.created_at).toLocaleDateString()}
                      </div>
                      <button
                        onClick={() => removeHistory(h.id)}
                        className="text-neutral-400 hover:text-red-600"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right: drafts */}
        <div className="lg:col-span-3 space-y-4">
          <div className="overline">Drafts</div>
          {!drafts ? (
            <div className="border border-dashed border-neutral-300 bg-white p-16 text-center">
              <Sparkles className="w-8 h-8 mx-auto mb-3 text-neutral-400" />
              <div className="font-display text-xl mb-2">Nothing generated yet</div>
              <p className="text-neutral-500 text-sm">Fill in the topic on the left and hit generate.</p>
            </div>
          ) : (
            Object.entries(drafts).map(([platform, text]) => {
              const p = PLATFORMS.find((x) => x.key === platform);
              if (!p) return null;
              const Icon = p.icon;
              return (
                <div
                  key={platform}
                  data-testid={MARKETING.draftPanel(platform)}
                  className="bg-white border border-neutral-200"
                >
                  <div className="flex items-center justify-between px-5 py-3 border-b border-neutral-200" style={{ background: "#FAFAFA" }}>
                    <div className="flex items-center gap-2 font-semibold text-sm">
                      <Icon className="w-4 h-4" style={{ color: p.color }} />
                      {p.label}
                    </div>
                    <button
                      data-testid={MARKETING.copyBtn(platform)}
                      onClick={() => copyDraft(platform, text)}
                      className="text-xs font-medium flex items-center gap-1 px-3 py-1.5 border border-neutral-300 hover:border-black hover:bg-white transition"
                    >
                      {copied === platform ? (
                        <><Check className="w-3 h-3" /> Copied</>
                      ) : (
                        <><Copy className="w-3 h-3" /> Copy</>
                      )}
                    </button>
                  </div>
                  <pre className="whitespace-pre-wrap font-sans text-sm p-5 leading-relaxed">
                    {text || "(empty)"}
                  </pre>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold tracking-widest uppercase text-neutral-500 mb-1.5">{label}</span>
      {children}
    </label>
  );
}
